import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Crown, CreditCard, FileText, Target, CheckSquare,
  Search, ShieldCheck, ShieldX, ArrowLeft, TrendingUp, UserPlus,
  Activity, Globe, Eye, BarChart3, Filter, TrendingDown, Loader2, ArrowDownCircle
} from 'lucide-react';

const ADMIN_SECRET_KEY = 'tg_admin_secret';

const TEST_PATTERNS = ['test', 'demo', 'example', '+', 'temp'];
const TEST_DOMAINS = ['mailinator', 'tempmail', 'fake', 'yopmail'];

const isTestAccount = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  const domain = lower.split('@')[1] || '';
  return TEST_PATTERNS.some(p => lower.includes(p)) || TEST_DOMAINS.some(d => domain.includes(d));
};

const MetricCard = ({ icon: Icon, label, value, color }) => (
  <Card className="p-4 flex items-center gap-3.5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`p-2 rounded-lg ${color}`}><Icon className="h-4 w-4 text-white" /></div>
    <div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  </Card>
);

// Onboarding funnel visualization
const FunnelSection = ({ funnel, loading, days, onDaysChange, hideTest }) => {
  const stages = funnel?.stages || [];
  const topCount = stages[0]?.count || 0;
  const overall = funnel?.overall_conversion_pct;

  return (
    <section data-testid="funnel-section">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          Onboarding Funnel
          {overall !== null && overall !== undefined && (
            <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold">
              {overall}% → Paid
            </span>
          )}
          {hideTest && <span className="text-[10px] text-slate-400 normal-case">(test accounts hidden)</span>}
        </h2>
        <div className="flex items-center gap-1.5" data-testid="funnel-range-filter">
          {[{k:'7',l:'7d'},{k:'30',l:'30d'},{k:'90',l:'90d'},{k:'all',l:'All time'}].map(opt => (
            <button
              key={opt.k}
              onClick={() => onDaysChange(opt.k)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                days === opt.k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`funnel-range-${opt.k}`}
            >{opt.l}</button>
          ))}
        </div>
      </div>

      <Card className="p-4 sm:p-5">
        {loading && !funnel ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading funnel…</div>
        ) : stages.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">No funnel events yet.</div>
        ) : (
          <div className="space-y-2.5">
            {stages.map((s, idx) => {
              const widthPct = topCount > 0 ? Math.max((s.count / topCount) * 100, s.count > 0 ? 4 : 0) : 0;
              const isDropoff = s.conversion_from_prev_pct !== null && s.conversion_from_prev_pct < 50 && idx > 0 && s.count > 0;
              return (
                <div key={s.key} className="group" data-testid={`funnel-stage-${s.key}`}>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="w-6 text-slate-400 font-mono tabular-nums">{String(idx + 1).padStart(2, '0')}</span>
                    <span className="flex-1 font-medium text-slate-700 truncate">{s.label}</span>
                    {s.conversion_from_prev_pct !== null && (
                      <span className={`text-[10px] tabular-nums ${isDropoff ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                        {isDropoff && <TrendingDown className="inline h-2.5 w-2.5 mr-0.5" />}
                        {s.conversion_from_prev_pct}% from prev
                      </span>
                    )}
                    <span className="text-slate-400 text-[10px] tabular-nums w-14 text-right">
                      {s.conversion_from_top_pct !== null ? `${s.conversion_from_top_pct}%` : '—'}
                    </span>
                    <span className="font-bold text-slate-900 tabular-nums w-14 text-right">{s.count}</span>
                  </div>
                  <div className="mt-1 ml-9 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        idx === 0 ? 'bg-blue-500' :
                        s.key === 'payment_success' ? 'bg-emerald-500' :
                        isDropoff ? 'bg-red-400' : 'bg-slate-400'
                      }`}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  {idx > 0 && s.drop_from_prev > 0 && (
                    <p className="ml-9 mt-1 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      Lost {s.drop_from_prev} {s.drop_from_prev === 1 ? 'user' : 'users'} at this step
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </section>
  );
};

export const AdminPage = () => {
  const navigate = useNavigate();
  const [secret, setSecret] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [hideTest, setHideTest] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelDays, setFunnelDays] = useState('all'); // 'all' | '7' | '30' | '90'

  // Plan-change dialog state
  const [planDialog, setPlanDialog] = useState({ open: false, user: null, mode: null }); // mode: 'upgrade' | 'downgrade'
  const [planDays, setPlanDays] = useState('365');
  const [planSaving, setPlanSaving] = useState(false);

  const openUpgradeDialog = (u) => { setPlanDays('365'); setPlanDialog({ open: true, user: u, mode: 'upgrade' }); };
  const openDowngradeDialog = (u) => setPlanDialog({ open: true, user: u, mode: 'downgrade' });
  const closePlanDialog = () => { if (!planSaving) setPlanDialog({ open: false, user: null, mode: null }); };

  const confirmPlanChange = async () => {
    if (!planDialog.user || !planDialog.mode) return;
    setPlanSaving(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const body = planDialog.mode === 'upgrade'
        ? { email: planDialog.user.email, plan: 'pro', days: parseInt(planDays, 10) }
        : { email: planDialog.user.email, plan: 'free' };
      const res = await fetch(`${API}/api/admin/upgrade-user?admin_secret=${encodeURIComponent(secret)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(json.detail || 'Plan change failed'); return; }
      // Refresh only the affected row locally (no full refetch)
      setData(prev => prev ? ({
        ...prev,
        users: prev.users.map(u => u.email === planDialog.user.email ? { ...u, plan: json.plan } : u),
      }) : prev);
      toast.success(planDialog.mode === 'upgrade'
        ? `${planDialog.user.email} upgraded to Pro (${planDays} days)`
        : `${planDialog.user.email} downgraded to Free`);
      setPlanDialog({ open: false, user: null, mode: null });
    } catch (e) {
      toast.error('Network error — please retry');
    } finally {
      setPlanSaving(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(ADMIN_SECRET_KEY);
    if (saved) { setSecret(saved); fetchData(saved); }
  }, []);

  // Fetch funnel whenever auth changes or filters change
  useEffect(() => {
    if (!authenticated || !secret) return;
    const fetchFunnel = async () => {
      setFunnelLoading(true);
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const params = new URLSearchParams({
          admin_secret: secret,
          hide_test: String(hideTest),
        });
        if (funnelDays !== 'all') params.append('days', funnelDays);
        const res = await fetch(`${API}/api/admin/funnel?${params}`);
        if (res.ok) setFunnel(await res.json());
      } catch { /* silent */ } finally { setFunnelLoading(false); }
    };
    fetchFunnel();
  }, [authenticated, secret, hideTest, funnelDays]);

  const fetchData = async (key) => {
    setLoading(true);
    setError('');
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/admin/dashboard?admin_secret=${encodeURIComponent(key)}`);
      if (res.status === 403) { setError('Invalid admin secret'); setAuthenticated(false); sessionStorage.removeItem(ADMIN_SECRET_KEY); setLoading(false); return; }
      const json = await res.json();
      setData(json);
      setAuthenticated(true);
      sessionStorage.setItem(ADMIN_SECRET_KEY, key);
    } catch { setError('Failed to connect'); }
    setLoading(false);
  };

  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    let list = data.users;
    if (hideTest) list = list.filter(u => !isTestAccount(u.email));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u => u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q));
    }
    return list;
  }, [data, search, hideTest]);

  const realUserCount = useMemo(() => {
    if (!data?.users) return 0;
    return data.users.filter(u => !isTestAccount(u.email)).length;
  }, [data]);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm p-8" data-testid="admin-login-card">
          <div className="text-center mb-6">
            <div className="inline-flex p-3 bg-slate-900 rounded-xl mb-3"><ShieldCheck className="h-6 w-6 text-white" /></div>
            <h1 className="text-xl font-bold text-slate-900">Admin Access</h1>
            <p className="text-sm text-slate-500 mt-1">Enter admin secret to continue</p>
          </div>
          {error && <p className="text-sm text-red-600 text-center mb-3" data-testid="admin-error">{error}</p>}
          <form onSubmit={(e) => { e.preventDefault(); fetchData(secret); }}>
            <Input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Admin secret" className="mb-3" data-testid="admin-secret-input" />
            <Button type="submit" className="w-full" disabled={loading || !secret} data-testid="admin-login-btn">
              {loading ? 'Verifying...' : 'Access Dashboard'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  const b = data?.business || {};
  const u = data?.usage || {};
  const t = data?.traffic || {};
  const sources = data?.sources || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="admin-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-lg font-bold text-slate-900">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchData(secret)} data-testid="admin-refresh-btn">Refresh</Button>
            <Button variant="outline" size="sm" onClick={() => { sessionStorage.removeItem(ADMIN_SECRET_KEY); setAuthenticated(false); setData(null); }} data-testid="admin-logout-btn">Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Business Metrics */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Business</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
            <MetricCard icon={Users} label="Total Users" value={b.total_users || 0} color="bg-slate-700" />
            <MetricCard icon={UserPlus} label="Real Users" value={realUserCount} color="bg-blue-600" />
            <MetricCard icon={Users} label="Free" value={b.free_users || 0} color="bg-slate-500" />
            <MetricCard icon={Crown} label="Pro" value={b.pro_users || 0} color="bg-amber-500" />
            <MetricCard icon={TrendingUp} label="New Today" value={b.new_today || 0} color="bg-emerald-600" />
            <MetricCard icon={CreditCard} label="Payments" value={b.total_payments || 0} color="bg-violet-600" />
            <MetricCard icon={Activity} label="Active Today" value={b.active_today || 0} color="bg-sky-600" />
            <MetricCard icon={Activity} label="Active Week" value={b.active_week || 0} color="bg-indigo-500" />
          </div>
        </section>

        {/* Usage + Traffic Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section>
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Usage</h2>
            <div className="grid grid-cols-3 gap-2.5">
              <MetricCard icon={FileText} label="Requirements" value={u.requirements || 0} color="bg-sky-600" />
              <MetricCard icon={Target} label="Scenarios" value={u.scenarios || 0} color="bg-orange-500" />
              <MetricCard icon={CheckSquare} label="Test Cases" value={u.testcases || 0} color="bg-teal-600" />
            </div>
          </section>
          <section>
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Traffic</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <MetricCard icon={Eye} label="Total Visits" value={t.total_visits || 0} color="bg-purple-600" />
              <MetricCard icon={Globe} label="Unique Visitors" value={t.unique_visitors || 0} color="bg-pink-600" />
              <MetricCard icon={UserPlus} label="Signups" value={t.signups || 0} color="bg-emerald-600" />
              <MetricCard icon={BarChart3} label="Conv. Rate" value={`${t.conversion_rate || 0}%`} color="bg-rose-500" />
            </div>
          </section>
        </div>

        {/* Signup Sources */}
        {Object.keys(sources).length > 0 && (
          <section>
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Signup Sources</h2>
            <Card className="p-4">
              <div className="flex flex-wrap gap-3">
                {Object.entries(sources).sort((a, b) => b[1] - a[1]).map(([src, cnt]) => (
                  <div key={src} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">{src}</span>
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{cnt}</span>
                  </div>
                ))}
              </div>
            </Card>
          </section>
        )}

        {/* Onboarding Funnel */}
        <FunnelSection
          funnel={funnel}
          loading={funnelLoading}
          days={funnelDays}
          onDaysChange={setFunnelDays}
          hideTest={hideTest}
        />

        {/* Users Table */}
        <section>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Users ({filteredUsers.length})</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="hide-test-toggle">
                <input type="checkbox" checked={hideTest} onChange={e => setHideTest(e.target.checked)} className="rounded border-slate-300" />
                <Filter className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs text-slate-500">Hide test accounts</span>
              </label>
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search email or name" className="pl-9 h-8 text-xs" data-testid="admin-user-search" />
              </div>
            </div>
          </div>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-users-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-3 py-2.5 font-medium text-slate-600">Email</th>
                    <th className="text-left px-3 py-2.5 font-medium text-slate-600">Name</th>
                    <th className="text-left px-3 py-2.5 font-medium text-slate-600">Signed Up</th>
                    <th className="text-left px-3 py-2.5 font-medium text-slate-600">Last Login</th>
                    <th className="text-left px-3 py-2.5 font-medium text-slate-600">Plan</th>
                    <th className="text-right px-3 py-2.5 font-medium text-slate-600">Proj</th>
                    <th className="text-right px-3 py-2.5 font-medium text-slate-600">Reqs</th>
                    <th className="text-right px-3 py-2.5 font-medium text-slate-600">Scn</th>
                    <th className="text-right px-3 py-2.5 font-medium text-slate-600">TCs</th>
                    <th className="text-left px-3 py-2.5 font-medium text-slate-600">Source</th>
                    <th className="text-center px-3 py-2.5 font-medium text-slate-600">Verified</th>
                    <th className="text-right px-3 py-2.5 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-medium text-slate-900 max-w-[200px] truncate">{u.email}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[120px] truncate">{u.full_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${u.plan === 'pro' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                          {u.plan === 'pro' && <Crown className="h-2.5 w-2.5" />}{u.plan === 'pro' ? 'Pro' : 'Free'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{u.projects || 0}</td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{u.requirements || 0}</td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{u.scenarios || 0}</td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{u.testcases || 0}</td>
                      <td className="px-3 py-2 text-slate-500">{u.signup_source || '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {u.email_verified ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 mx-auto" /> : <ShieldX className="h-3.5 w-3.5 text-slate-300 mx-auto" />}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {u.plan === 'pro' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[10px] gap-1 text-slate-700 hover:bg-slate-100"
                            onClick={() => openDowngradeDialog(u)}
                            data-testid={`downgrade-btn-${u.email}`}
                          >
                            <ArrowDownCircle className="h-3 w-3" />Downgrade
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="h-7 px-2 text-[10px] gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => openUpgradeDialog(u)}
                            data-testid={`upgrade-btn-${u.email}`}
                          >
                            <Crown className="h-3 w-3" />Upgrade
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      </main>

      {/* Plan-change confirmation dialog */}
      <Dialog open={planDialog.open} onOpenChange={(v) => !v && closePlanDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="plan-change-dialog">
          {planDialog.mode === 'upgrade' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" />Upgrade to Pro</DialogTitle>
                <DialogDescription className="pt-1">
                  Upgrade <span className="font-semibold text-slate-800">{planDialog.user?.email}</span> to the Pro plan.
                  This gives the workspace 500 requirements / 2,000 scenarios / 5,000 test cases.
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <label className="text-xs font-medium text-slate-600 block mb-1.5">Pro validity</label>
                <Select value={planDays} onValueChange={setPlanDays}>
                  <SelectTrigger className="h-9" data-testid="plan-validity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Days</SelectItem>
                    <SelectItem value="90">90 Days</SelectItem>
                    <SelectItem value="365">365 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closePlanDialog} disabled={planSaving} data-testid="plan-dialog-cancel">Cancel</Button>
                <Button onClick={confirmPlanChange} disabled={planSaving} className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" data-testid="plan-dialog-confirm">
                  {planSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                  Upgrade to Pro
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><ArrowDownCircle className="h-4 w-4 text-slate-500" />Downgrade to Free</DialogTitle>
                <DialogDescription className="pt-1">
                  Downgrade <span className="font-semibold text-slate-800">{planDialog.user?.email}</span> to the Free plan.
                  Existing projects, requirements and test cases are preserved. Their workspace limits will reset to 50 / 200 / 500.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={closePlanDialog} disabled={planSaving} data-testid="plan-dialog-cancel">Cancel</Button>
                <Button onClick={confirmPlanChange} disabled={planSaving} variant="destructive" className="gap-1.5" data-testid="plan-dialog-confirm">
                  {planSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownCircle className="h-4 w-4" />}
                  Downgrade to Free
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
